import {
  MATRIX_LABEL_PATTERN,
  MAX_EXPRESSION_SOURCE_LENGTH,
  MAX_MATRIX_LABEL_LENGTH,
  MAX_VECTOR_LABEL_LENGTH,
  VECTOR_COLOR_PATTERN,
  VECTOR_LABEL_PATTERN,
} from "./equation-policy";
import type { Dimension } from "@src/math/types";
import type { DraftMatrixFor, DraftVectorFor } from "./equation";

export function nodeId(value: unknown): string {
  if (typeof value !== "string" || value.length === 0)
    throw new Error("Invalid equation node ID");
  return value;
}

export function matrixLabel(value: unknown): string {
  return matchedString(
    value,
    MATRIX_LABEL_PATTERN,
    MAX_MATRIX_LABEL_LENGTH,
    "matrix label",
  );
}

export function vectorLabel(value: unknown): string {
  return matchedString(
    value,
    VECTOR_LABEL_PATTERN,
    MAX_VECTOR_LABEL_LENGTH,
    "vector label",
  );
}

export function vectorColor(value: unknown): string {
  return matchedString(
    value,
    VECTOR_COLOR_PATTERN,
    7,
    "vector color",
  ).toLowerCase();
}

export function expressionDraft(value: unknown): string {
  if (typeof value !== "string" || value.length > MAX_EXPRESSION_SOURCE_LENGTH)
    throw new Error("Invalid expression draft");
  return value;
}

export function draftMatrixValues<D extends Dimension>(
  dimension: D,
  values: readonly string[],
): DraftMatrixFor<D> {
  assertStringShape(values, dimension ** 2);
  const parsed = values.map(expressionDraft);
  return (dimension === 2
    ? [parsed[0], parsed[1], parsed[2], parsed[3]]
    : [
        parsed[0],
        parsed[1],
        parsed[2],
        parsed[3],
        parsed[4],
        parsed[5],
        parsed[6],
        parsed[7],
        parsed[8],
      ]) as unknown as DraftMatrixFor<D>;
}

export function draftVectorValues<D extends Dimension>(
  dimension: D,
  values: readonly string[],
): DraftVectorFor<D> {
  assertStringShape(values, dimension);
  const parsed = values.map(expressionDraft);
  return (dimension === 2
    ? [parsed[0], parsed[1]]
    : [parsed[0], parsed[1], parsed[2]]) as unknown as DraftVectorFor<D>;
}

export function matrixEntryIndex(dimension: Dimension, value: number): number {
  assertIndex(value, dimension ** 2);
  return value;
}

export function vectorCoordinateIndex(
  dimension: Dimension,
  value: number,
): number {
  assertIndex(value, dimension);
  return value;
}

function matchedString(
  value: unknown,
  pattern: RegExp,
  maximumLength: number,
  description: string,
): string {
  if (
    typeof value !== "string" ||
    value.length > maximumLength ||
    !pattern.test(value)
  )
    throw new Error(`Invalid ${description}`);
  return value;
}

function assertIndex(value: number, length: number): void {
  if (!Number.isInteger(value) || value < 0 || value >= length)
    throw new Error(`Invalid collection index: ${String(value)}`);
}

function assertStringShape(values: readonly string[], length: number): void {
  if (
    values.length !== length ||
    !values.every((value) => typeof value === "string")
  )
    throw new Error("Invalid draft values");
}
